import React, { useState, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import SplitPane from './SplitPane';
import MonthView from './MonthView';
import WeekView2 from './WeekView2';
import SafeMarkdown from './SafeMarkdown';
import { generateEventSlug, getLocalDateKey, parseEventDate } from '../utils/eventUtils';

function CalendarView({ curMonth, curYear, events, onModalStateChange, selectedEventSlug, onModalClose, isModalOpen, theme, onMonthChange, onYearChange, viewMode, onViewModeChange, currentWeek, selectedEvent: externalSelectedEvent, onEventSelect, onRightPaneChange, combinedEvents, isRightPaneOpen: externalIsRightPaneOpen, clearSelectedEventSlug }) {
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [internalIsRightPaneOpen, setInternalIsRightPaneOpen] = useState(!!selectedEventSlug);
    const [starredState, setStarredState] = useState(false);
    const [isCrossMonthSwipeLoading, setIsCrossMonthSwipeLoading] = useState(false);
    const isSwipeMonthTransitionRef = useRef(false);

    const formatDayHeading = (date) => {
        const parsedDate = parseEventDate(date);
        if (!parsedDate) {
            return 'Selected day';
        }
        return parsedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    };

    const formatTimeRange = (startInput, endInput) => {
        const start = parseEventDate(startInput);
        const end = parseEventDate(endInput || startInput);
        if (!start || !end) {
            return '';
        }
        return `${start.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} - ${end.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}`;
    };

    const normalizeDescription = (description) => {
        return typeof description === 'string' ? description : '';
    };

    const normalizeTags = (tags) => {
        if (Array.isArray(tags)) {
            return tags;
        }
        if (typeof tags === 'string') {
            return tags.split(',').map(tag => ({ name: tag.trim() })).filter(tag => tag.name);
        }
        return [];
    };

    const safeText = (value, fallback = '') => {
        if (typeof value === 'string' || typeof value === 'number') {
            return String(value);
        }
        return fallback;
    };

    useEffect(() => {
        if (selectedEvent) {
            const starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
            setStarredState(starred.includes(generateEventSlug(selectedEvent)));
        } else {
            setStarredState(false);
        }
    }, [selectedEvent]);

    // Use external state if provided, otherwise use internal state
    const isRightPaneOpen = externalIsRightPaneOpen !== undefined ? externalIsRightPaneOpen : internalIsRightPaneOpen;
    const setIsRightPaneOpen = onRightPaneChange || setInternalIsRightPaneOpen;

    // Reset sidebar when month/year changes
    useEffect(() => {
        if (isSwipeMonthTransitionRef.current) {
            isSwipeMonthTransitionRef.current = false;
            return;
        }

        setSelectedDay(null);
        setSelectedEvent(null);

        // if month/year is current month/year, set selected day to today
        if (curMonth === new Date().getMonth() + 1 && curYear === new Date().getFullYear()) {
            setSelectedDay(new Date());
        }

    }, [curMonth, curYear]);

    // Handle external event selection (from slug navigation)
    useEffect(() => {
        if (externalSelectedEvent) {
            const eventDate = parseEventDate(externalSelectedEvent.startDate) || new Date();
            setSelectedDay(eventDate);
            setSelectedEvent(externalSelectedEvent);
            setIsRightPaneOpen(true);
            
            // Update URL hash with event slug
            //const eventSlug = generateEventSlug(externalSelectedEvent);
            //if (window.location.hash !== `#${eventSlug}`) {
            //    window.history.pushState({}, '', `#${eventSlug}`);
            //}
        }
    }, [externalSelectedEvent]);

    // Ensure right pane is open when we have a selectedEventSlug
    useEffect(() => {
        if (selectedEventSlug && !isRightPaneOpen) {
            //console.log('Forcing right pane open for selectedEventSlug');
            setIsRightPaneOpen(true);
        }
    }, [selectedEventSlug, isRightPaneOpen]);

    // Stop showing the tiny right-pane loader once the selected day's month is loaded.
    useEffect(() => {
        if (!isCrossMonthSwipeLoading || !selectedDay) return;

        const selectedMonth = selectedDay.getMonth() + 1;
        const selectedYear = selectedDay.getFullYear();
        if (curMonth === selectedMonth && curYear === selectedYear) {
            setIsCrossMonthSwipeLoading(false);
        }
    }, [isCrossMonthSwipeLoading, selectedDay, curMonth, curYear]);

    // Handle selectedEventSlug navigation
    useEffect(() => {
        if (selectedEventSlug && events && events.length > 0) {
            //console.log('Processing selectedEventSlug in CalendarView:', selectedEventSlug);
            // Find the event by slug - check both regular events and combined events
            let targetEvent = events.find(event => {
                const eventSlug = generateEventSlug(event);
                return eventSlug === selectedEventSlug;
            });

            // If not found in regular events and we have combined events (week view), check there too
            if (!targetEvent && combinedEvents && combinedEvents.length > 0) {
                targetEvent = combinedEvents.find(event => {
                    const eventSlug = generateEventSlug(event);
                    return eventSlug === selectedEventSlug;
                });
            }

            if (targetEvent) {
                //console.log('Found target event in CalendarView:', targetEvent.title);
                // Set the selected day to the event's date
                const eventDate = parseEventDate(targetEvent.startDate) || new Date();
                setSelectedDay(eventDate);
                setSelectedEvent(targetEvent);
                setIsRightPaneOpen(true);
                
                // Update URL hash with event slug (in case it wasn't already set)
                const eventSlug = generateEventSlug(targetEvent);
                if (window.location.hash !== `#${eventSlug}`) {
                    window.history.pushState({}, '', `#${eventSlug}`);
                }
                
                // Call external callback if provided
                if (onEventSelect) {
                    onEventSelect(targetEvent);
                }
            } else {
                //console.log('Event not found in CalendarView for slug:', selectedEventSlug);
            }
        }
    }, [selectedEventSlug, events, combinedEvents, onEventSelect]);

    // Notify parent when right pane state changes
    useEffect(() => {
        if (onRightPaneChange) {
            onRightPaneChange(isRightPaneOpen);
        }        

    }, [isRightPaneOpen, onRightPaneChange]);

    // Clear selections when external right pane is closed
    useEffect(() => {
        if (externalIsRightPaneOpen !== undefined && !externalIsRightPaneOpen) {
            setSelectedDay(null);
            setSelectedEvent(null);
            
        }
    }, [externalIsRightPaneOpen]);

    // Get the current week's dates when in week view
    const getCurrentWeekDates = () => {
        const today = new Date();
        const currentDay = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - currentDay);
        
        const weekDates = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            weekDates.push(date);
        }
        return weekDates;
    };

    // Get events for the current week
    const getCurrentWeekEvents = () => {
        const weekDates = currentWeek ? (() => {
            const dates = [];
            for (let i = 0; i < 7; i++) {
                const date = new Date(currentWeek);
                date.setDate(currentWeek.getDate() + i);
                dates.push(date);
            }
            return dates;
        })() : (selectedWeek || getCurrentWeekDates());
        const weekDateStrs = weekDates.map(d => getLocalDateKey(d));
        return events.filter(e => weekDateStrs.includes(getLocalDateKey(e.startDate)));
    };

    // Get events for a specific day
    const getEventsForDay = (day) => {
        if (!day) return [];
        const dayStr = getLocalDateKey(day);
        return events.filter(event => getLocalDateKey(event.startDate) === dayStr);
    };
   

    // Handle day selection
    const handleDaySelect = (day) => {
        // Get events for the selected day
        const dayEvents = getEventsForDay(day);
        
        // On small screens, only allow clicking on days that have events
        if (window.innerWidth < 768 && dayEvents.length === 0) {
            return; // Don't do anything if no events on small screens
        }
        
        setSelectedDay(day);
        setIsRightPaneOpen(true);
        
        // If there's exactly one event, automatically select it
        if (dayEvents.length === 1) {
            const singleEvent = dayEvents[0];
            setSelectedEvent(singleEvent);
            // Update URL hash with event slug
            const eventSlug = generateEventSlug(singleEvent);
            if (window.location.hash !== `#${eventSlug}`) {
                window.history.pushState({}, '', `#${eventSlug}`);
            }
            // Call external callback if provided
            if (onEventSelect) {
                onEventSelect(singleEvent);
            }
        } else {
            setSelectedEvent(null);
            // Clear URL hash when no specific event is selected
            window.history.pushState({}, '', window.location.pathname);
        }
    };

    // Handle event selection
    const handleEventSelect = (event, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        //console.log('handleEventSelect', event);

        // If this is a different event than the one from the slug, clear the slug to allow normal navigation
        if (selectedEventSlug) {
            const currentEventSlug = generateEventSlug(event);
            if (currentEventSlug !== selectedEventSlug) {
                //console.log('User selected different event, clearing selectedEventSlug for normal navigation');
                // Clear the slug to allow normal navigation
                // We'll update the URL hash below, but clear the slug state
                if (clearSelectedEventSlug) {
                    clearSelectedEventSlug();
                }
            }
        }

        //console.log('handleEventSelect: selectedEventSlug', selectedEventSlug);
        
        // Set the selected day to the event's date
        const eventDate = parseEventDate(event.startDate) || new Date();
        setSelectedDay(eventDate);
        setSelectedEvent(event);
        setIsRightPaneOpen(true);
        
        // Update URL hash with event slug
        const eventSlug = generateEventSlug(event);
        if (window.location.hash !== `#${eventSlug}`) {
            window.history.pushState({}, '', `#${eventSlug}`);
        }
        
        // Call external callback if provided
        if (onEventSelect) {
            onEventSelect(event);
        }

        scrollToEvent(event);
    };

    const scrollToEvent = (event) => {
        const eventElement = document.getElementById(`event-${generateEventSlug(event)}`);
        if (eventElement) {
            eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // Handle pane state change
    const handlePaneStateChange = (isOpen) => {
        // Only prevent closing the pane if we have a selectedEventSlug and we're trying to close it
        // Allow opening the pane normally
        //console.log('handlePaneStateChange', isOpen);
        //if (!isOpen && selectedEventSlug) {
            //console.log('Preventing right pane close due to selectedEventSlug');
            //return;
        //}
        
        setIsRightPaneOpen(isOpen);

        if (isOpen === false) {
            // Clear selections when pane is closed
            setSelectedDay(null);
            setSelectedEvent(null);
            // Clear URL hash when closing pane
            window.history.pushState({}, '', window.location.pathname);
            // Clear the selectedEventSlug to allow normal navigation
            if (clearSelectedEventSlug) {
                clearSelectedEventSlug();
            }
        }
    };

    const handleLinkClick = (e, url) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleShare = (event) => {
        const slug = generateEventSlug(event);
        const url = `${window.location.origin}/#${slug}`;
        navigator.clipboard.writeText('Check out this event I found on ATLTech.events: ' + url).then(() => {
            // Show a toast notification
            if (window.showToast) {
                window.showToast('Event URL copied to clipboard!');
            }
        }).catch(err => {
            //console.error('Failed to copy URL:', err);
        });
    };

    const renderTextWithLinks = (text) => {
        if (!text) return null;
        
        // URL regex pattern
        const urlPattern = /(https?:\/\/[^\s]+)/g;
        
        // Split text by URLs and map to elements
        const parts = text.split(urlPattern);
        return parts.map((part, index) => {
            if (part.match(urlPattern)) {
                return (
                    <a
                        key={index}
                        href={part}
                        onClick={(e) => handleLinkClick(e, part)}
                        className={`${theme.link} underline break-all`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {part}
                    </a>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    const renderNavigationTitle = () => {
        if (viewMode === 'month') {
            const date = new Date(curYear, curMonth - 1, 1);
            return date.toLocaleString('default', { month: 'long', year: 'numeric' });
        } else {
            const startDate = selectedWeek?.[0] || getCurrentWeekDates()[0];
            const endDate = selectedWeek?.[6] || getCurrentWeekDates()[6];
            return `${startDate.toLocaleString('default', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleString('default', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
    };

    const renderEventCount = (events, day) => {
        if (!events || events.length === 0) return null;
        return (
            <div className="flex items-center gap-1 justify-center mt-2 mb-2 block lg:hidden">
                <button 
                    className={`${theme.eventCountBg} min-w-8 rounded-full p-1 ${theme.buttonHover} transition-colors`}
                    aria-label={`Show ${events.length} events for this day`}
                    onClick={() => handleDaySelect(day)}
                >
                    {events.length}
                </button>
            </div>
        );
    };

    const renderEventList = (events, day) => {
        if (!events || events.length === 0) return null;
        return (
            <div className="hidden lg:flex flex-col h-full">     
                {events.slice(0, 3).map(event => (
                    <div
                        key={event.id}
                        className={`${theme.eventBg} ${theme.border} border rounded mb-1 cursor-pointer ${
                            event.eventType === 'Featured' ? `border-yellow-400 ${theme.featuredDay}` : ''
                        }`}
                        onClick={(e) => handleEventSelect(event, e)}
                        aria-label={`View details for ${event.title} by ${event.organization}`}
                        title={`${event.title} — ${event.organization}`}
                    >
                        <div className="px-1 py-0.5 text-xs">
                            <div className="font-medium truncate">{event.title}</div>
                            <div className="opacity-75 truncate">{event.organization}</div>
                        </div>
                    </div>
                ))}

                {events.length > 3 && (
                    <div 
                        aria-label={`Show ${events.length} events for this day`}  
                        className={`text-xs italic hidden lg:block mt-auto sticky bottom-0 left-0 right-0 text-center ${theme.eventCountBg} ${theme.buttonHover} cursor-pointer`}                    
                        onClick={() => handleDaySelect(day)}
                    >
                        +{events.length - 3} more
                    </div>
                )}
            </div>
        );
    };

    const renderDayContent = (day, events) => {
        const dayEvents = events.filter(event => 
            new Date(event.startDate).toDateString() === day.toDateString()
        );
        
        return (
            <div className="h-10 md:h-full flex flex-col overflow-hidden md:overflow-y-auto">
                {renderEventCount(dayEvents, day)}
                {renderEventList(dayEvents, day)}
            </div>
        );
    };

    const leftContent = (
        <div className={`flex flex-col h-full bg-[url('atltechevents-bg.png')] bg-cover bg-center bg-no-repeat  `}>
            

            {/* Calendar View */}
            <div className="flex-1 min-h-0">
                {viewMode === 'month' ? (
                    <MonthView
                        curMonth={curMonth}
                        curYear={curYear}
                        events={events}
                        theme={theme}
                        onDaySelect={handleDaySelect}
                        onEventSelect={handleEventSelect}
                        renderDayContent={renderDayContent}
                        selectedDay={selectedDay}
                    />
                ) : (
                    <WeekView2
                        days={currentWeek ? (() => {
                            const dates = [];
                            for (let i = 0; i < 7; i++) {
                                const date = new Date(currentWeek);
                                date.setDate(currentWeek.getDate() + i);
                                dates.push(date);
                            }
                            return dates;
                        })() : (selectedWeek || getCurrentWeekDates())}
                        events={getCurrentWeekEvents()}
                        theme={theme}
                        onDaySelect={handleDaySelect}
                        onEventSelect={handleEventSelect}
                        renderDayContent={renderDayContent}
                    />
                )}
            </div>
        </div>
    );

    // Swipe left/right on the right pane navigates to the next/prev day on mobile
    const navigateDayByOffset = (offset) => {
        if (!selectedDay) return;
        const newDay = new Date(selectedDay);
        newDay.setDate(newDay.getDate() + offset);

        const nextMonth = newDay.getMonth() + 1;
        const nextYear = newDay.getFullYear();
        const monthChanged = nextMonth !== curMonth || nextYear !== curYear;

        if (monthChanged) {
            setIsCrossMonthSwipeLoading(true);
            isSwipeMonthTransitionRef.current = true;
            if (nextYear !== curYear && onYearChange) onYearChange(nextYear, { preserveRightPane: true });
            if ((nextMonth !== curMonth || nextYear !== curYear) && onMonthChange) onMonthChange(nextMonth, { preserveRightPane: true });
        } else {
            setIsCrossMonthSwipeLoading(false);
        }

        setSelectedDay(newDay);
        setSelectedEvent(null);
        window.history.pushState({}, '', window.location.pathname);
        // Auto-select if there's exactly one event on the new day
        const dayEvents = getEventsForDay(newDay);
        if (dayEvents.length === 1) {
            const singleEvent = dayEvents[0];
            setSelectedEvent(singleEvent);
            window.history.pushState({}, '', `#${generateEventSlug(singleEvent)}`);
            if (onEventSelect) onEventSelect(singleEvent);
        }
    };

    const rightPaneSwipeHandlers = useSwipeable({
        onSwipedLeft: () => { if (window.innerWidth < 768 && viewMode !== 'week') navigateDayByOffset(1); },
        onSwipedRight: () => { if (window.innerWidth < 768 && viewMode !== 'week') navigateDayByOffset(-1); },
        preventScrollOnSwipe: false,
        trackMouse: false,
    });

    const rightContent = (
        <div data-day-swipe-zone="true" {...rightPaneSwipeHandlers} className={`h-full flex flex-col p-4 overflow-hidden ${theme.background} ${theme.text}`}>
            {selectedDay ? (
                // Show day's events with event details in bottom half
                <div className="flex-1 flex flex-col min-h-0">
                    {/* Events List - Top Half */}
                    <div className={`overflow-y-auto min-h-0 ${selectedEvent ? 'h-[50%]' : 'flex-1'}`}>
                        <h2 className={`text-lg font-semibold mb-4 sticky top-0 ${theme.background} pb-2`}>
                            {formatDayHeading(selectedDay)}
                        </h2>
                        {isCrossMonthSwipeLoading && (
                            <div className={`text-xs ${theme.secondaryText} mb-3 flex items-center gap-2`} role="status" aria-live="polite">
                                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                                <span>Loading next month...</span>
                            </div>
                        )}
                        <div className="space-y-4">
                            {getEventsForDay(selectedDay).map(event => (
                                <div
                                    key={event.id}
                                    className={`${theme.eventBg} ${theme.border} border-2 ml-1 mr-1 p-1 lg:p-1 shadow-lg cursor-pointer hover:shadow-xl transition-shadow ${
                                        selectedEvent?.id === event.id ? 'ring-2 ring-blue-500 ' : ''
                                    }`}
                                    id={`event-${generateEventSlug(event)}`}
                                    onClick={(e) => handleEventSelect(event, e)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleEventSelect(event, e);
                                        }
                                    }}
                                >
                                    {event.eventType === 'Featured' && (
                                        <span className="bg-yellow-400 text-black text-xs px-2 py-1 rounded-full mb-2 inline-block" aria-label="Featured event">
                                            <i className="fas fa-star mr-1" aria-hidden="true"></i>
                                            Featured
                                        </span>
                                    )}
                                    <h4 className="font-bold text-sm mb-2">{safeText(event.title, 'Event')}</h4>
                                    <div className={`text-sm ${theme.secondaryText} mb-2`}>
                                        <div>{safeText(event.organization, 'Unknown organization')}</div>
                                        {(() => {
                                            const parsedStart = parseEventDate(event.startDate);
                                            return parsedStart && parsedStart.getHours() === 0 && parsedStart.getMinutes() === 0;
                                        })() ? (
                                            <div>All day</div>
                                        ) : (
                                            <div>{formatTimeRange(event.startDate, event.endDate)}</div>
                                        )}
                                    </div>                                    
                                </div>
                            ))}
                            {getEventsForDay(selectedDay).length === 0 && (
                                <div className={`text-center ${theme.secondaryText} py-8`}>
                                    No events scheduled for this day
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Event Details - Bottom Half */}
                    {selectedEvent && (
                        <div className={`h-[100%] md:h-[75%] border-t mt-4 p-4 flex flex-col ${theme.detailPaneBg} ${theme.text} min-h-0 overflow-y-auto`}>
                            <div className={`flex justify-between min-w-full mb-4 pb-4 border-b ${theme.panelBorder} pb-2 items-center`}>
                                <h3 className="min-w-[60%] text-lg font-semibold">{safeText(selectedEvent.title, 'Event Details')}</h3>
                                <button
                                    onClick={() => {
                                        const starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
                                        const eventSlug = generateEventSlug(selectedEvent);
                                        let newStarred;
                                        if (starred.includes(eventSlug)) {
                                            newStarred = starred.filter(slug => slug !== eventSlug);
                                        } else {
                                            newStarred = [...starred, eventSlug];
                                        }
                                        localStorage.setItem('starredEvents', JSON.stringify(newStarred));
                                        // Force re-render
                                        setStarredState(newStarred.includes(eventSlug));
                                    }}
                                    aria-label={starredState ? 'Unfavorite this event' : 'Favorite this event'}
                                    className="text-red-500 text-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-400 rounded hover:scale-110 transition-transform mr-4"
                                    style={{ minWidth: 32 }}
                                >
                                    <i className={starredState ? 'fas fa-heart' : 'far fa-heart'} aria-hidden="true"></i>
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedEvent(null);
                                        // Clear URL hash when closing event
                                        window.history.pushState({}, '', window.location.pathname);
                                    }}
                                    className={`${theme.secondaryText} ${theme.buttonHover} ml-2 mr-2`}
                                    aria-label="Close event details"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="flex-1 min-h-0">
                                <div className="space-y-4">
                                    <p className={`text-sm ${theme.secondaryText}`}>{safeText(selectedEvent.organization, 'Unknown organization')}</p>

                                    {selectedEvent.eventType === 'Featured' && (
                                        <span className="bg-yellow-400 text-black text-xs px-2 py-1 rounded-full mb-2 inline-block" aria-label="Featured event">
                                            <i className="fas fa-star mr-1" aria-hidden="true"></i>
                                            Featured
                                        </span>
                                    )}

                                {selectedEvent.description && (
                                    <div className="mb-4 max-h-32 overflow-y-auto">
                                        <SafeMarkdown text={normalizeDescription(selectedEvent.description)} theme={theme} />
                                    </div>
                                )}

                                {(() => {
                                    const selectedStart = parseEventDate(selectedEvent.startDate);
                                    const selectedEnd = parseEventDate(selectedEvent.endDate || selectedEvent.startDate);
                                    if (!selectedStart || !selectedEnd) return null;
                                    if (selectedStart.getHours() === 0 && selectedStart.getMinutes() === 0) return null;
                                    return (
                                        <p className="mb-4"><strong>Time:</strong> {selectedStart.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' })} to {selectedEnd.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' })}</p>
                                    );
                                })()}
                                {selectedEvent.location && (
                                    <p className="mb-4"><strong>Location:</strong> {safeText(selectedEvent.location)}</p>
                                )}
                                {normalizeTags(selectedEvent.tags).length > 0 && (
                                    <div className="mb-4 text-center">
                                        <div className="flex flex-wrap gap-2 mt-2 justify-center">
                                            {normalizeTags(selectedEvent.tags).map((tag, index) => (
                                                <span 
                                                    key={index} 
                                                    className={`${theme.tag} text-xs font-medium px-2.5 py-0.5 rounded`}
                                                >
                                                    {typeof tag === 'string' ? tag : tag?.name || ''}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                    {selectedEvent.eventURL && (() => {
                                        const eventEnd = parseEventDate(selectedEvent.endDate || selectedEvent.startDate);
                                        if (!eventEnd) return false;
                                        return eventEnd >= new Date(new Date().setHours(0, 0, 0, 0));
                                    })() && (
                                <div className="text-center py-3">
                                        <a 
                                            href={selectedEvent.eventURL} 
                                            className={`inline-block ${theme.button} py-2 px-4 rounded transition-colors`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                        >
                                            {selectedEvent.eventURL === '/private-events' ? 'About private events' : 'Go to event page'}
                                        </a>
                                        </div>
                                    )}
                                {selectedEvent.sponsorLogo && (
                                    <div className="mt-2 text-center mb-2">
                                        <span className={`text-xs ${theme.secondaryText}`}>Event Hosted By</span>
                                        <a href={selectedEvent.sponsorURL} target="_blank" rel="noopener noreferrer" className="block mt-1">
                                            <img src={selectedEvent.sponsorLogo} alt={selectedEvent.sponsorName || 'Event Host'} className="max-h-8 mx-auto" />
                                        </a>
                                    </div>
                                )}

                                <div className={`block text-center px-4 py-2 border-t ${theme.border}`}>
                                    <button
                                        onClick={() => handleShare(selectedEvent)}
                                        className={`${theme.link} text-sm text-center gap-2`}
                                    >
                                        <i className="fas fa-share-alt"></i>&nbsp;
                                        Share This (Copy to Clipboard)
                                    </button>
                                </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                // Default view when no day is selected
                <div className={`flex-1 flex items-center justify-center ${theme.secondaryText}`}>
                    <div className="text-center">
                        <i className="fas fa-calendar-alt text-4xl mb-4"></i>
                        <p>Select a day to view its events</p>
                        <p className="text-sm mt-2">Click an event to see its details</p>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className={`h-full ${theme.background} ${theme.text}`}>
            <SplitPane
                leftContent={leftContent}
                rightContent={rightContent}
                defaultRightPaneWidth={350}
                isRightPaneOpen={isRightPaneOpen}
                onPaneStateChange={handlePaneStateChange}
            />
        </div>
    );
}

export default CalendarView; 